import { useReducer, useCallback } from "react";

export let MAX_UNDO = 30;
export function setMaxUndo(n) { MAX_UNDO = n; }

const initialState = {
  nodes: [],
  edges: [],
  rigCables: [],
  undoStack: [],
  redoStack: [],
};

function pushUndo(state) {
  const snapshot = { nodes: state.nodes, edges: state.edges, rigCables: state.rigCables };
  const stack = [snapshot, ...state.undoStack].slice(0, MAX_UNDO);
  return stack;
}

function allEdgeRigCableRefs(edge) {
  const chain = edge.data?.cableChain;
  if (chain && chain.length > 0) {
    return chain.map((entry) => entry.rigCableId || entry.rigCableTempId).filter(Boolean);
  }
  const ref = edge.data?.rigCableId || edge.data?.rigCableTempId;
  return ref ? [ref] : [];
}

function isRigCableOrphan(rigCable, edges, excludeEdgeId) {
  const ref = rigCable.id || rigCable.tempId;
  return !edges.some((e) => e.id !== excludeEdgeId && allEdgeRigCableRefs(e).includes(ref));
}

function removeOrphanRigCable(rigCables, edges, oldRef, excludeEdgeId) {
  if (!oldRef) return rigCables;
  const rc = rigCables.find((r) => (r.id || r.tempId) === oldRef);
  if (!rc) return rigCables;
  if (isRigCableOrphan(rc, edges, excludeEdgeId)) {
    return rigCables.filter((r) => r !== rc);
  }
  return rigCables;
}

function boardReducer(state, action) {
  switch (action.type) {
    case "ADD_DEVICE":
      return {
        ...state,
        nodes: [...state.nodes, action.node],
        undoStack: pushUndo(state),
        redoStack: [],
      };
    case "UPDATE_BOUNDARY": {
      const { nodeId, updates } = action;
      return {
        ...state,
        nodes: state.nodes.map((n) => {
          if (n.id !== nodeId) return n;
          return {
            ...n,
            data: { ...n.data, boundary: { ...n.data.boundary, ...updates } },
          };
        }),
        undoStack: pushUndo(state),
        redoStack: [],
      };
    }
    case "ADD_BOUNDARY":
      return {
        ...state,
        nodes: [...state.nodes, action.node],
        undoStack: pushUndo(state),
        redoStack: [],
      };
    case "ADD_CANVAS_LABEL":
      return {
        ...state,
        nodes: [...state.nodes, action.node],
        undoStack: pushUndo(state),
        redoStack: [],
      };
    case "UPDATE_CANVAS_LABEL": {
      const { nodeId, updates } = action;
      return {
        ...state,
        nodes: state.nodes.map((n) => {
          if (n.id !== nodeId) return n;
          const updatedLabel = { ...n.data.canvasLabel, ...updates };
          return {
            ...n,
            data: { ...n.data, canvasLabel: updatedLabel },
            zIndex: -100 + (updatedLabel.sort_order || 0),
          };
        }),
        undoStack: pushUndo(state),
        redoStack: [],
      };
    }
    case "SWAP_CANVAS_LABEL_ORDER": {
      const { nodeIdA, orderA, nodeIdB, orderB } = action;
      return {
        ...state,
        nodes: state.nodes.map((n) => {
          if (n.id === nodeIdA) {
            const updated = { ...n.data.canvasLabel, sort_order: orderA };
            return { ...n, data: { ...n.data, canvasLabel: updated }, zIndex: -100 + (orderA || 0) };
          }
          if (n.id === nodeIdB) {
            const updated = { ...n.data.canvasLabel, sort_order: orderB };
            return { ...n, data: { ...n.data, canvasLabel: updated }, zIndex: -100 + (orderB || 0) };
          }
          return n;
        }),
        undoStack: pushUndo(state),
        redoStack: [],
      };
    }
    case "ADD_SYSTEM": {
      const newRigCables = action.rigCables
        ? [...state.rigCables, ...action.rigCables]
        : state.rigCables;
      return {
        ...state,
        nodes: [...state.nodes, ...action.nodes],
        edges: [...state.edges, ...action.edges],
        rigCables: newRigCables,
        undoStack: pushUndo(state),
        redoStack: [],
      };
    }
    case "REMOVE_DEVICE": {
      const nodeId = action.nodeId;
      return {
        ...state,
        nodes: state.nodes.filter((n) => n.id !== nodeId),
        edges: state.edges.filter(
          (e) => e.source !== nodeId && e.target !== nodeId
        ),
        undoStack: pushUndo(state),
        redoStack: [],
      };
    }
    case "ADD_CONNECTION": {
      const newRigCables = action.rigCable
        ? [...state.rigCables, action.rigCable]
        : state.rigCables;
      return {
        ...state,
        edges: [...state.edges, action.edge],
        rigCables: newRigCables,
        undoStack: pushUndo(state),
        redoStack: [],
      };
    }
    case "REMOVE_CONNECTION": {
      const removedEdge = state.edges.find((e) => e.id === action.edgeId);
      const remainingEdges = state.edges.filter((e) => e.id !== action.edgeId);
      let cleanedCables = state.rigCables;
      if (removedEdge) {
        for (const ref of allEdgeRigCableRefs(removedEdge)) {
          cleanedCables = removeOrphanRigCable(cleanedCables, remainingEdges, ref, null);
        }
      }
      return {
        ...state,
        edges: remainingEdges,
        rigCables: cleanedCables,
        undoStack: pushUndo(state),
        redoStack: [],
      };
    }
    case "RECONNECT_CONNECTION":
      return {
        ...state,
        edges: [
          ...state.edges.filter((e) => e.id !== action.oldEdgeId),
          action.newEdge,
        ],
        undoStack: pushUndo(state),
        redoStack: [],
      };
    case "UPDATE_EDGE_DATA":
      return {
        ...state,
        edges: state.edges.map((e) => {
          if (e.id !== action.edgeId) return e;
          return { ...e, data: { ...e.data, ...action.updates } };
        }),
        undoStack: pushUndo(state),
        redoStack: [],
      };
    case "ASSIGN_CABLE": {
      const { edgeId, cable, suppliedCable } = action;
      const targetEdge = state.edges.find((e) => e.id === edgeId);
      if (!targetEdge) return state;

      const oldRefs = allEdgeRigCableRefs(targetEdge);

      if (cable) {
        const tempId = `rc-${crypto.randomUUID()}`;
        const newRigCable = { tempId, cableId: cable.id, cable, label: null };
        const updatedEdges = state.edges.map((e) => {
          if (e.id !== edgeId) return e;
          return {
            ...e,
            data: {
              ...e.data,
              cableChain: [{ rigCableTempId: tempId, cableId: cable.id, cable }],
              suppliedCable: false,
            },
          };
        });
        let cleanedCables = state.rigCables;
        for (const ref of oldRefs) {
          cleanedCables = removeOrphanRigCable(cleanedCables, updatedEdges, ref, null);
        }
        return {
          ...state,
          edges: updatedEdges,
          rigCables: [...cleanedCables, newRigCable],
          undoStack: pushUndo(state),
          redoStack: [],
        };
      }

      // Unassign cable
      const updatedEdges = state.edges.map((e) => {
        if (e.id !== edgeId) return e;
        return {
          ...e,
          data: {
            ...e.data,
            cableChain: [],
            suppliedCable: suppliedCable || false,
          },
        };
      });
      let unassignCleaned = state.rigCables;
      for (const ref of oldRefs) {
        unassignCleaned = removeOrphanRigCable(unassignCleaned, updatedEdges, ref, null);
      }
      return {
        ...state,
        edges: updatedEdges,
        rigCables: unassignCleaned,
        undoStack: pushUndo(state),
        redoStack: [],
      };
    }
    case "ADD_CHAIN_CABLE": {
      const { edgeId, cable } = action;
      const targetEdge = state.edges.find((e) => e.id === edgeId);
      if (!targetEdge) return state;

      const tempId = `rc-${crypto.randomUUID()}`;
      const newRigCable = { tempId, cableId: cable.id, cable, label: null };
      const existingChain = targetEdge.data?.cableChain || [];
      const newEntry = { rigCableTempId: tempId, cableId: cable.id, cable };
      const newChain = [...existingChain, newEntry];

      const updatedEdges = state.edges.map((e) => {
        if (e.id !== edgeId) return e;
        return {
          ...e,
          data: {
            ...e.data,
            cableChain: newChain,
          },
        };
      });

      return {
        ...state,
        edges: updatedEdges,
        rigCables: [...state.rigCables, newRigCable],
        undoStack: pushUndo(state),
        redoStack: [],
      };
    }
    case "REMOVE_CHAIN_CABLE": {
      const { edgeId, position } = action;
      const targetEdge = state.edges.find((e) => e.id === edgeId);
      if (!targetEdge) return state;

      const existingChain = targetEdge.data?.cableChain || [];
      const removed = existingChain[position];
      const newChain = existingChain.filter((_, i) => i !== position);

      const updatedEdges = state.edges.map((e) => {
        if (e.id !== edgeId) return e;
        return {
          ...e,
          data: {
            ...e.data,
            cableChain: newChain,
          },
        };
      });

      const removedRef = removed?.rigCableId || removed?.rigCableTempId;
      return {
        ...state,
        edges: updatedEdges,
        rigCables: removeOrphanRigCable(state.rigCables, updatedEdges, removedRef, null),
        undoStack: pushUndo(state),
        redoStack: [],
      };
    }
    case "DRAG_END":
      return {
        ...state,
        undoStack: [action.snapshot, ...state.undoStack].slice(0, MAX_UNDO),
        redoStack: [],
      };
    case "UNDO": {
      if (state.undoStack.length === 0) return state;
      const [restored, ...rest] = state.undoStack;
      const current = { nodes: state.nodes, edges: state.edges, rigCables: state.rigCables };
      return {
        nodes: restored.nodes,
        edges: restored.edges,
        rigCables: restored.rigCables || [],
        undoStack: rest,
        redoStack: [current, ...state.redoStack].slice(0, MAX_UNDO),
      };
    }
    case "REDO": {
      if (state.redoStack.length === 0) return state;
      const [restored, ...rest] = state.redoStack;
      return {
        nodes: restored.nodes,
        edges: restored.edges,
        rigCables: restored.rigCables || [],
        undoStack: [
          { nodes: state.nodes, edges: state.edges, rigCables: state.rigCables },
          ...state.undoStack,
        ].slice(0, MAX_UNDO),
        redoStack: rest,
      };
    }
    case "LOAD_STATE":
      return {
        nodes: action.state.nodes,
        edges: action.state.edges,
        rigCables: action.state.rigCables || [],
        undoStack: [],
        redoStack: [],
      };
    case "APPLY_NODE_CHANGES":
      return { ...state, nodes: action.nodes };
    case "APPLY_EDGE_CHANGES":
      return { ...state, edges: action.edges };
    case "SET_RIG_CABLES":
      return {
        ...state,
        rigCables: action.rigCables,
        undoStack: pushUndo(state),
        redoStack: [],
      };
    case "UPDATE_DEVICE_LABEL":
      return {
        ...state,
        nodes: state.nodes.map((n) =>
          n.id === action.nodeId
            ? { ...n, data: { ...n.data, label: action.label } }
            : n
        ),
        undoStack: pushUndo(state),
        redoStack: [],
      };
    case "UPDATE_EXCLUDED_PORT": {
      return {
        ...state,
        nodes: state.nodes.map((n) => {
          if (n.id !== action.nodeId) return n;
          const prev = { ...(n.data.excludedPorts || {}) };
          if (action.note === undefined) {
            delete prev[String(action.portId)];
          } else {
            prev[String(action.portId)] = action.note;
          }
          return { ...n, data: { ...n.data, excludedPorts: prev } };
        }),
        undoStack: pushUndo(state),
        redoStack: [],
      };
    }
    default:
      return state;
  }
}

export default function useBoardState() {
  const [state, dispatch] = useReducer(boardReducer, initialState);

  const addDevice = useCallback(
    (node) => dispatch({ type: "ADD_DEVICE", node }),
    []
  );

  const updateBoundary = useCallback(
    (nodeId, updates) => dispatch({ type: "UPDATE_BOUNDARY", nodeId, updates }),
    []
  );

  const addBoundary = useCallback(
    (node) => dispatch({ type: "ADD_BOUNDARY", node }),
    []
  );

  const addCanvasLabel = useCallback(
    (node) => dispatch({ type: "ADD_CANVAS_LABEL", node }),
    []
  );

  const updateCanvasLabel = useCallback(
    (nodeId, updates) => dispatch({ type: "UPDATE_CANVAS_LABEL", nodeId, updates }),
    []
  );

  const swapCanvasLabelOrder = useCallback(
    (nodeIdA, orderA, nodeIdB, orderB) => dispatch({ type: "SWAP_CANVAS_LABEL_ORDER", nodeIdA, orderA, nodeIdB, orderB }),
    []
  );

  const addSystem = useCallback(
    (nodes, edges, rigCables) => dispatch({ type: "ADD_SYSTEM", nodes, edges, rigCables }),
    []
  );

  const removeDevice = useCallback(
    (nodeId) => dispatch({ type: "REMOVE_DEVICE", nodeId }),
    []
  );

  const addConnection = useCallback(
    (edge, rigCable) => dispatch({ type: "ADD_CONNECTION", edge, rigCable }),
    []
  );

  const removeConnection = useCallback(
    (edgeId) => dispatch({ type: "REMOVE_CONNECTION", edgeId }),
    []
  );

  const reconnectConnection = useCallback(
    (oldEdgeId, newEdge) =>
      dispatch({ type: "RECONNECT_CONNECTION", oldEdgeId, newEdge }),
    []
  );

  const updateEdgeData = useCallback(
    (edgeId, updates) => dispatch({ type: "UPDATE_EDGE_DATA", edgeId, updates }),
    []
  );

  const applyNodeChanges = useCallback(
    (nodes) => dispatch({ type: "APPLY_NODE_CHANGES", nodes }),
    []
  );

  const applyEdgeChanges = useCallback(
    (edges) => dispatch({ type: "APPLY_EDGE_CHANGES", edges }),
    []
  );

  const setRigCables = useCallback(
    (rigCables) => dispatch({ type: "SET_RIG_CABLES", rigCables }),
    []
  );

  const assignCable = useCallback(
    (edgeId, cable, suppliedCable) =>
      dispatch({ type: "ASSIGN_CABLE", edgeId, cable, suppliedCable }),
    []
  );

  const addChainCable = useCallback(
    (edgeId, cable) => dispatch({ type: "ADD_CHAIN_CABLE", edgeId, cable }),
    []
  );

  const removeChainCable = useCallback(
    (edgeId, position) => dispatch({ type: "REMOVE_CHAIN_CABLE", edgeId, position }),
    []
  );

  const updateDeviceLabel = useCallback(
    (nodeId, label) => dispatch({ type: "UPDATE_DEVICE_LABEL", nodeId, label }),
    []
  );

  const updateExcludedPort = useCallback(
    (nodeId, portId, note) => dispatch({ type: "UPDATE_EXCLUDED_PORT", nodeId, portId, note }),
    []
  );

  const dragEnd = useCallback(
    (snapshot) => dispatch({ type: "DRAG_END", snapshot }),
    []
  );

  const undo = useCallback(() => dispatch({ type: "UNDO" }), []);
  const redo = useCallback(() => dispatch({ type: "REDO" }), []);

  const loadState = useCallback(
    (newState) => dispatch({ type: "LOAD_STATE", state: newState }),
    []
  );

  return {
    nodes: state.nodes,
    edges: state.edges,
    rigCables: state.rigCables,
    canUndo: state.undoStack.length > 0,
    canRedo: state.redoStack.length > 0,
    addDevice,
    addBoundary,
    updateBoundary,
    addCanvasLabel,
    updateCanvasLabel,
    swapCanvasLabelOrder,
    addSystem,
    removeDevice,
    addConnection,
    removeConnection,
    reconnectConnection,
    updateEdgeData,
    applyNodeChanges,
    applyEdgeChanges,
    setRigCables,
    assignCable,
    addChainCable,
    removeChainCable,
    updateDeviceLabel,
    updateExcludedPort,
    dragEnd,
    undo,
    redo,
    loadState,
  };
}
